import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface Blocklist {
  id: string
  name: string
  objectState: string
  creatives: string[]
  lineItems: string[]
  advertisers: string[]
  advertiserDomains: string[]
  iabCategories: string[]
  iabCategoryAction: string
  ownerTeamId: string
  ownerTeamName: string
  validationWarnings: ValidationWarning[]
  createTime: string
  updateTime: string
}

export interface BlocklistInput {
  name: string
  creatives?: string[]
  lineItems?: string[]
  advertisers?: string[]
  advertiserDomains?: string[]
  iabCategories?: string[]
  iabCategoryAction?: string
  ownerTeamId?: string
  objectState?: string
}

function mapBlocklist(r: Record<string, unknown>): Blocklist {
  const team = r.team as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    creatives: (r.creatives as string[]) ?? [],
    lineItems: (r.lineItems as string[]) ?? [],
    advertisers: (r.advertisers as string[]) ?? [],
    advertiserDomains: (r.advertiserDomains as string[]) ?? [],
    iabCategories: (r.iabCategories as string[]) ?? [],
    iabCategoryAction: (r.iabCategoryAction as string) ?? '',
    ownerTeamId: (team?.id as string) ?? '',
    ownerTeamName: (team?.name as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
  }
}

/** The options a blocklist list page is narrowed by on the server. */
export interface BlocklistListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of blocklists, narrowed by search and state on the server. */
async function fetchBlocklistPage(
  search: string | undefined,
  status: ObjectStatus | undefined,
  page: number,
  pageSize: number,
  sort?: ListSort | null
): Promise<ListPage<Blocklist>> {
  const params = new URLSearchParams({
    ...searchParams('blocklists', search),
    concise: 'true',
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/blocklists?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch blocklists')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapBlocklist)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const BlocklistsApi = {
  /** One page of blocklists, filtered by search and state on the server. */
  getPage: ({ page = 1, pageSize = 25, search, status, sort }: BlocklistListOptions = {}) =>
    fetchBlocklistPage(search, status, page, pageSize, sort),

  /** Every blocklist matching the filters, walked page by page — for downloads. */
  getAllPages: (search?: string, status?: ObjectStatus): Promise<Blocklist[]> =>
    fetchAllPages((page, pageSize) => fetchBlocklistPage(search, status, page, pageSize)),

  getAll: async (search?: string): Promise<Blocklist[]> => {
    const params = new URLSearchParams({
      ...searchParams('blocklists', search),
      concise: 'true',
      context: getContext(),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'true',
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/blocklists?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch blocklists')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapBlocklist)
  },

  getById: async (id: string): Promise<Blocklist> => {
    const res = await apiFetch(`${API_BASE}/blocklists/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch blocklist')
    return mapBlocklist(await res.json())
  },

  create: async (id: string, input: BlocklistInput): Promise<Blocklist> => {
    const { ownerTeamId, ...rest } = input
    const body: Record<string, unknown> = { id, ...rest }
    if (ownerTeamId) body.team = { id: ownerTeamId }
    const res = await apiFetch(`${API_BASE}/blocklists/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create blocklist')
    return mapBlocklist(await res.json())
  },

  update: async (id: string, input: BlocklistInput): Promise<Blocklist> => {
    const { ownerTeamId, ...rest } = input
    const body: Record<string, unknown> = { ...rest }
    if (ownerTeamId) body.team = { id: ownerTeamId }
    const res = await apiFetch(`${API_BASE}/blocklists/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save blocklist')
    return mapBlocklist(await res.json())
  },
}
